import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { LessonModeBadge } from '../components/LessonModeBadge';
import {
  ArrowLeft,
  Search,
  CheckCircle2,
  Clock,
  XCircle,
  Users,
  TrendingUp,
  Download,
  Filter,
  Radio,
  Loader2,
  AlertCircle,
  Copy,
  Check
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { getSession, getLessonSlides, getSessionParticipants, getSessionGrades } from '../../utils/supabase/queries';

interface StudentProgress {
  id: string;
  name: string;
  email: string;
  progress: number;
  status: 'completed' | 'in-progress' | 'not-started';
  lastAccessed?: Date;
  completedAt?: Date;
  score?: number;
}

export default function StudentPacedMonitoring() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState<StudentProgress[]>([]);
  const [lessonTitle, setLessonTitle] = useState('');
  const [totalSlides, setTotalSlides] = useState(0);
  const [sessionCode, setSessionCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Load session data and participants
  useEffect(() => {
    if (sessionId) {
      loadSessionData();
      // Set up refresh interval for real-time updates
      const refreshInterval = setInterval(loadSessionData, 5000);
      return () => clearInterval(refreshInterval);
    }
  }, [sessionId]);

  const loadSessionData = async () => {
    if (!sessionId) return;

    try {
      // Load session info
      const sessionData = await getSession(sessionId);
      if (!sessionData) {
        setError('Session not found');
        setLoading(false);
        return;
      }

      // Load lesson slides
      const slidesData = await getLessonSlides(sessionData.lesson_id);
      setTotalSlides(slidesData.length);
      setLessonTitle((sessionData as any).lessons?.title || 'Unnamed Lesson');
      setSessionCode(sessionData.session_code || '');

      // Load participants
      const participantsData = await getSessionParticipants(sessionId);

      // Load grades for all participants
      const gradesData = await getSessionGrades(sessionId);
      const gradesMap = new Map(gradesData.map(g => [g.student_name, g]));

      // Transform participants to StudentProgress
      const studentsList = participantsData.map((p, idx) => {
        const grade = gradesMap.get(p.student_name);
        let status: 'completed' | 'in-progress' | 'not-started' = 'not-started';

        if (p.progress_percentage === 100) {
          status = 'completed';
        } else if (p.progress_percentage > 0) {
          status = 'in-progress';
        }

        return {
          id: p.id,
          name: p.student_name,
          email: `student-${idx}@school.edu`, // Email not stored in session_participants
          progress: p.progress_percentage || 0,
          status,
          lastAccessed: p.last_active ? new Date(p.last_active) : undefined,
          completedAt: status === 'completed' ? new Date() : undefined,
          score: grade?.total_score ? Math.round((grade.total_score / (grade.max_possible_score || 100)) * 100) : undefined,
        };
      });

      setStudents(studentsList);
      setLoading(false);
    } catch (err) {
      console.error('Error loading session data:', err);
      setError('Failed to load session data');
      toast.error('Failed to load student progress data');
      setLoading(false);
    }
  };

  const completedCount = students.filter(s => s.status === 'completed').length;
  const inProgressCount = students.filter(s => s.status === 'in-progress').length;
  const averageProgress = students.length > 0 ? Math.round(
    students.reduce((sum, s) => sum + s.progress, 0) / students.length
  ) : 0;

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCopyCode = () => {
    navigator.clipboard.writeText(sessionCode);
    setCopied(true);
    toast.success('Access code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusBadge = (status: StudentProgress['status']) => {
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 rounded-lg">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Completed
          </Badge>
        );
      case 'in-progress':
        return (
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 rounded-lg">
            <Clock className="w-3 h-3 mr-1" />
            In Progress
          </Badge>
        );
      case 'not-started':
        return (
          <Badge className="bg-gray-100 text-gray-700 border-gray-200 rounded-lg">
            <XCircle className="w-3 h-3 mr-1" />
            Not Started
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading session data...</p>
        </div>
      </div>
    );
  }

  if (error || !sessionId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">{error || 'Session not found'}</h2>
          <Button onClick={() => navigate('/teacher/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2 sm:gap-3 mb-3 sm:mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/teacher/dashboard')}
              className="rounded-xl text-xs sm:text-sm p-2"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button variant="outline" className="rounded-xl text-xs sm:text-sm p-2 sm:px-4" size="sm">
                <Filter className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Filter</span>
              </Button>
              <Button variant="outline" className="rounded-xl text-xs sm:text-sm p-2 sm:px-4" size="sm">
                <Download className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Export</span>
              </Button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 sm:gap-3 mb-1 flex-wrap">
                <h1 className="text-lg sm:text-2xl font-bold truncate">{lessonTitle || 'Student-Paced Lesson'}</h1>
                <LessonModeBadge mode="student-paced" />
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {totalSlides} slides • {students.length} students
              </p>
            </div>
            {sessionCode && (
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <Badge className="bg-primary/10 text-primary border-primary/30 rounded-lg px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm">
                  <code className="font-mono font-semibold">{sessionCode}</code>
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyCode}
                  className="rounded-lg p-2"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-card border border-border rounded-xl p-3 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-muted-foreground">Total Students</p>
              <Users className="w-4 h-4 text-primary flex-shrink-0" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold">{students.length}</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-3 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-muted-foreground">Completed</p>
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-emerald-600">{completedCount}</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-3 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-muted-foreground">In Progress</p>
              <Clock className="w-4 h-4 text-orange-600 flex-shrink-0" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-orange-600">{inProgressCount}</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-3 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-muted-foreground">Avg Progress</p>
              <TrendingUp className="w-4 h-4 text-primary flex-shrink-0" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold">{averageProgress}%</p>
          </div>
        </div>

        {/* Student List */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-border">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg font-semibold">Student Progress</h2>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl text-sm"
              />
            </div>
          </div>

          <ScrollArea className="h-[500px]">
            <div className="divide-y divide-border">
              {filteredStudents.map((student) => (
                <div key={student.id} className="p-3 sm:p-6 hover:bg-accent/50 transition-colors">
                  <div className="flex items-start justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-xs sm:text-base truncate">{student.name}</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground truncate">{student.email}</p>
                    </div>
                    {getStatusBadge(student.status)}
                  </div>

                  <div className="space-y-1 sm:space-y-2">
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{student.progress}%</span>
                    </div>
                    <Progress value={student.progress} className="h-2" />
                  </div>

                  <div className="mt-2 sm:mt-3 flex items-center justify-between flex-wrap gap-2 text-xs sm:text-sm">
                    <div>
                      {student.status === 'completed' && student.completedAt && (
                        <p className="text-muted-foreground">
                          Completed {format(student.completedAt, 'MMM d, yyyy')}
                        </p>
                      )}
                      {student.status === 'in-progress' && student.lastAccessed && (
                        <p className="text-muted-foreground">
                          Last accessed {format(student.lastAccessed, 'MMM d, yyyy')}
                        </p>
                      )}
                      {student.status === 'not-started' && (
                        <p className="text-muted-foreground">Not started yet</p>
                      )}
                    </div>

                    {student.score !== undefined && (
                      <Badge variant="outline" className="rounded-lg text-xs flex-shrink-0">
                        Score: {student.score}%
                      </Badge>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl mt-2 sm:mt-3 w-full text-xs"
                    onClick={() => {}}
                  >
                    View Responses
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}